import React, { useMemo, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import "./periodPicker.css";
import { ko } from "date-fns/locale";

const EMPTY_RANGE = Object.freeze({ from: null, to: null });

export const toKstIsoRange = (from, to) => {
  if (!from) return {};
  const fmt = (d, end = false) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = end ? "23:59:59.999" : "00:00:00";
    return new Date(`${y}-${m}-${dd}T${hh}+09:00`).toISOString();
  };
  const s = fmt(from, false);
  const e = fmt(to || from, true);
  return { join_start: s, join_end: e, join_start_date: s, join_end_date: e };
};

export default function PeriodPicker({
  mode = "single",
  fromYear = 2018,
  toYear = new Date().getFullYear() + 1,
  defaultMonth,
  value,
  onChange,
  onConfirm,
  onCancel,
}) {
  const [single, setSingle] = useState(null);
  const [range, setRange] = useState({ from: null, to: null });

  const isControlled = value !== undefined;

  const curSingle = mode === "single"
    ? (isControlled ? value ?? null : single)
    : null;

  const curRange = useMemo(() => {
    if (mode !== "range") return EMPTY_RANGE;
    return isControlled ? (value ?? EMPTY_RANGE) : range;
  }, [mode, isControlled, value, range]);

  const display = useMemo(() => {
    const fmt = (d) => d?.toLocaleDateString("ko-KR") ?? "";
    if (mode === "single") return curSingle ? fmt(curSingle) : "날짜를 선택하세요.";
    const { from, to } = curRange;
    if (!from) return "날짜를 선택하세요.";
    return `${fmt(from)} ~ ${to ? fmt(to) : ""}`;
  }, [mode, curSingle, curRange]);

  const handleSelect = (val) => {
    if (onChange) {
      onChange(val ?? (mode === "single" ? null : { from: null, to: null }));
    } else {
      if (mode === "single") setSingle(val ?? null);
      else setRange(val ?? { from: null, to: null });
    }
  };

  const handleConfirm = () => {
    const r = mode === "single"
      ? { from: curSingle, to: curSingle }
      : { from: curRange.from, to: curRange.to || curRange.from };
    const params = toKstIsoRange(r.from, r.to);
    onConfirm?.(params, r);
  };

  const fromMonth = new Date(fromYear, 0, 1);
  const toMonth = new Date(toYear, 11, 31);

  const KO_DAYS = ["S", "M", "T", "W", "T", "F", "S"];
  const formatters = {
    formatWeekdayName: (date) => KO_DAYS[date.getDay()],
  };

  return (
    <div className="pp-card">
      {mode === "range" && (
        <div className="pp-header">
          <div className="pp-title">기간설정</div>
          <div className="pp-inputs">
            <input
              className="pp-input"
              readOnly
              placeholder="날짜를 선택하세요."
              value={display.includes("~") ? display.split("~")[0].trim() : (display || "")}
            />
            <span className="pp-tilde">~</span>
            <input
              className="pp-input"
              readOnly
              placeholder="날짜를 선택하세요."
              value={display.includes("~") ? (display.split("~")[1]?.trim() ?? "") : ""}
            />
          </div>
        </div>
      )}

      <DayPicker
        locale={ko}
        formatters={formatters}
        mode={mode}
        selected={mode === "single" ? curSingle : curRange}
        onSelect={handleSelect}
        defaultMonth={defaultMonth}
        captionLayout="dropdown"
        fromYear={fromYear}
        toYear={toYear}
        fromMonth={fromMonth}
        toMonth={toMonth}
        pagedNavigation
        weekStartsOn={0}
        showOutsideDays
        className="pp-rdp"
        /* ✅ CSS가 져도 인라인이 이깁니다 */
        styles={{
          nav:          { display: 'flex', gap: '8px' },
          nav_button:   { color: '#9ca3af', background: 'transparent', width: 24, height: 24, borderRadius: 9999, border: 0 },
          nav_icon:     { color: '#9ca3af' },
          caption:      { padding: '0 4px' },
          caption_dropdowns: { display: 'flex', alignItems: 'center', gap: '8px' },
          dropdown:     { appearance: 'auto', WebkitAppearance: 'auto', MozAppearance: 'auto', font: 'inherit', lineHeight: 'inherit', background: 'transparent', border: 0, paddingRight: 0 },
        }}
      />

      <div className="pp-actions">
        {/* {onCancel && <button className="pp-cancel" type="button" onClick={onCancel}>취소</button>} */}
        <button
          className="pp-confirm"
          type="button"
          onClick={handleConfirm}
          disabled={mode === "single" ? !curSingle : !(curRange && curRange.from)}
        >
          확인
        </button>
      </div>
    </div>
  );
}
